import { ElectronApi } from '@/services/electron.api';
import { canUse } from '@/utils/can-use.utils';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { random, round } from 'lodash-es';
import { LunarUtil, Solar } from 'lunar-javascript';
import { DataSourceFormat, DataSourceKey } from './enum';

export const sexagenaryCycleArr = [
  '甲子',
  '乙丑',
  '丙寅',
  '丁卯',
  '戊辰',
  '己巳',
  '庚午',
  '辛未',
  '壬申',
  '癸酉',
  '甲戌',
  '乙亥',
  '丙子',
  '丁丑',
  '戊寅',
  '己卯',
  '庚辰',
  '辛巳',
  '壬午',
  '癸未',
  '甲申',
  '乙酉',
  '丙戌',
  '丁亥',
  '戊子',
  '己丑',
  '庚寅',
  '辛卯',
  '壬辰',
  '癸巳',
  '甲午',
  '乙未',
  '丙申',
  '丁酉',
  '戊戌',
  '己亥',
  '庚子',
  '辛丑',
  '壬寅',
  '癸卯',
  '甲辰',
  '乙巳',
  '丙午',
  '丁未',
  '戊申',
  '己酉',
  '庚戌',
  '辛亥',
  '壬子',
  '癸丑',
  '甲寅',
  '乙卯',
  '丙辰',
  '丁巳',
  '戊午',
  '己未',
  '庚申',
  '辛酉',
  '壬戌',
  '癸亥'
];

export const getChineseTimeRange = () => {
  const range: string[] = [];
  for (let i = 0; i <= 22; i = i + 2) {
    const hm = (i < 10 ? '0' + i : i) + ':50';
    range.push(`${LunarUtil.convertTime(hm)}时`);
  }
  return range;
};
/**
 * 双时区城市列表
 */
export const dualtimeCityList: { name: string; brief: string }[] = [
  { name: '格林威治标准时间', brief: 'GMT' },
  { name: '惠灵顿', brief: 'WLG' },
  { name: '悉尼', brief: 'SYD' },
  { name: '东京', brief: 'TYO' },
  { name: '首尔', brief: 'SEL' },
  { name: '北京', brief: 'BJS' },
  { name: '香港', brief: 'HKG' },
  { name: '新加坡', brief: 'SIN' },
  { name: '曼谷', brief: 'BKK' },
  { name: '新德里', brief: 'DEL' },
  { name: '伊斯兰堡', brief: 'ISB' },
  { name: '迪拜', brief: 'DXB' },
  { name: '莫斯科', brief: 'MOW' },
  { name: '开罗', brief: 'CAI' },
  { name: '开普敦', brief: 'CPT' },
  { name: '巴黎', brief: 'PAR' },
  { name: '伦敦', brief: 'LON' },
  { name: '纽约', brief: 'NYC' },
  { name: '芝加哥', brief: 'CHI' },
  { name: '洛杉矶', brief: 'LAX' },
  { name: '檀香山', brief: 'HNL' },
  { name: '雅典', brief: 'ATH' },
  { name: '德黑兰', brief: 'THR' },
  { name: '仰光', brief: 'RGN' },
  { name: '里约热内卢', brief: 'RIO' },
  { name: '新西兰夏令时', brief: 'NZDT' },
  { name: '国际日期变更线(东)', brief: 'IDLE' },
  { name: '新西兰标准时间', brief: 'NZT' },
  { name: '东澳大利亚夏令时', brief: 'AESST' },
  { name: '东澳大利亚标准时', brief: 'EAST' },
  { name: '关岛标准时', brief: 'GST' },
  { name: '东欧夏令时', brief: 'EEST' },
  { name: '中欧夏令时', brief: 'CEST' },
  { name: '东欧标准时', brief: 'EET' },
  { name: '巴黎夏令时', brief: 'PAR' },
  { name: '伦敦夏令时', brief: 'LON' },
  { name: '中欧标准时', brief: 'CET' },
  { name: '法国冬令时', brief: 'FWT' },
  { name: '西欧夏令时', brief: 'WEST' },
  { name: '西欧标准时间', brief: 'WET' },
  { name: '纽约夏令时', brief: 'NYC' },
  { name: '芝加哥夏令时', brief: 'CHI' },
  { name: '洛杉矶夏令时', brief: 'LAX' }
];

const _getDateWeekLabels = (options: {
  format?: 'dddd' | 'ddd';
  _case?: 'normal' | 'upper' | 'lower';
  cn?: boolean;
  cnFlag?: '周';
}) => {
  const { format, _case = 'normal', cn, cnFlag } = options;
  const res: string[] = [];
  for (let i = 0; i <= 6; i++) {
    let o = dayjs();
    if (cn) o = o.locale('zh-cn');
    let val = o.day(i).format(format ?? 'dddd');
    if (cnFlag == '周') val = val.replace('星期', '周');
    if (_case == 'lower') val = val.toLowerCase();
    if (_case == 'upper') val = val.toUpperCase();
    res.push(val);
  }
  return res;
};
const _getDateMonthLabels = (options: {
  format?: 'MMMM' | 'MMM';
  _case?: 'normal' | 'upper' | 'lower';
  cn?: boolean;
}) => {
  const { format, _case = 'normal', cn } = options;
  const res: string[] = [];
  for (let i = 0; i <= 11; i++) {
    let o = dayjs();
    if (cn) o = o.locale('zh-cn');
    let val = o.month(i).format(format ?? 'MMMM');
    if (_case == 'lower') val = val.toLowerCase();
    if (_case == 'upper') val = val.toUpperCase();
    res.push(val);
  }
  return res;
};

const _getLunarDayLabels = () => {
  const start = dayjs('2023-11-12');
  const res: string[] = [];
  for (let i = 1; i <= 30; i++) {
    const oDay = start.add(i, 'days');
    const o = Solar.fromYmd(oDay.year(), oDay.month() + 1, oDay.date());
    const lunar = o.getLunar();
    res.push(lunar.getDayInChinese());
  }
  return res;
};

const _getYearValue = () => dayjs().year();

export const dataSourceList: Record<string, DataSourceItem[]> = {
  时间日期: [
    {
      label: '时间',
      key: 'time',
      comment: '时和分（跟随系统24H/12H设置）',
      type: 'time',
      minValue: 0,
      maxValue: 24,
      canNotReachMaxValue: true,
      returnValue: '[00:00, 24:00)',
      unit: ':',
      allowShapeTypes: ['image', 'image-array-digit', 'text', 'progress-bar'],
      formats: [
        DataSourceFormat['Format_8:00'],
        DataSourceFormat['Format_08:00']
      ],
      value: () => Number(dayjs().format('H')),
      widgetImageArray: 'time',
      excludeDeviceTypes: ['M66']
    },
    {
      label: '小时',
      key: 'timeHour',
      comment: '小时（跟随系统24H/12H设置）',
      type: 'time',
      minValue: 0,
      maxValue: 24,
      returnValue: '[0, 24)',
      canNotReachMaxValue: true,
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image'].includes(shapeType);
            }
            case 'AOD': {
              return ['image'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        // 必须最后返回true
        return true;
      },
      value: () => {
        const day = dayjs();
        const hour = Number(day.format('H'));
        const minute = Number(day.format('m'));
        return hour + round(minute / 60, 1);
      },
      defaultValue: 9,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 10;
        else return 9;
      }
    },
    {
      label: '小时（12H）',
      key: 'timeHour12H',
      comment: '当前时间转换到12小时制后的时，范围[1, 12]',
      type: 'time',
      minValue: 1,
      maxValue: 12,
      returnValue: '[1, 12]',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      value: () => {
        const day = dayjs();
        const hour = Number(day.format('h'));
        const minute = Number(day.format('m'));
        return hour + round(minute / 60, 1);
      },
      defaultValue: 9,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 10;
        else return 9;
      },
      hidden: true,
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'N67',
        'N66',
        'N62',
        'N62S',
        'O61',
        'P65',
        // 'P62',
        'O62',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'M65S',
        'M62'
      ]
    },
    // 只在动效中显示的时区小时（24H）
    {
      label: '小时（24H）',
      key: 'timeHour24H',
      comment: '当前时间转换到24小时制后的时，范围[0,24)',
      type: 'time',
      minValue: 0,
      maxValue: 24,
      returnValue: '[0, 24)',
      canNotReachMaxValue: true,
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      value: () => {
        const day = dayjs();
        const hour = Number(day.format('H'));
        const minute = Number(day.format('m'));
        return hour + round(minute / 60, 1);
      },
      defaultValue: 9,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 10;
        else return 9;
      },
      hidden: true,
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'N67',
        'N66',
        'N62',
        'N62S',
        'P65',
        // 'P62',
        'O62',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'M65S',
        'M62'
      ]
    },
    // 普通元素和动效中都会显示
    {
      label: '小时（24H）',
      key: 'timeHour24H',
      comment: '当前时间对应24小时制，范围[0,24)',
      type: 'time',
      minValue: 0,
      maxValue: 24,
      returnValue: '[0, 24)',
      exportCheckIsSupport: true,
      canNotReachMaxValue: true,
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      value: () => {
        const day = dayjs();
        const hour = Number(day.format('H'));
        const minute = Number(day.format('m'));
        return hour + round(minute / 60, 1);
      },
      defaultValue: 9,
      includeDeviceTypes: ['O61', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '时辰',
      key: 'timeChineseTime',
      comment: '子时 丑时 寅时 ... 亥时',
      type: 'time',
      minValue: 0,
      maxValue: 11,
      returnValue: '子时 丑时 寅时 ... 亥时',
      allowShapeTypes: ['image-array-normal'],
      labelRange: getChineseTimeRange(),
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'N67',
        'N66',
        'N62',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'O65',
        'O65M',
        'O66',
        'M65S',
        'M62',
        'P67',
        'Q66'
      ]
    },
    {
      label: '小时十位',
      key: 'timeHourHigh',
      comment: '小时的十位数（跟随系统24H/12H设置）',
      minValue: 0,
      maxValue: 2,
      returnValue: '[0, 2]',
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('HH').slice(0, 1)),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 1;
        else return 0;
      },
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '小时个位',
      key: 'timeHourLow',
      comment: '小时的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('HH').slice(1)),
      defaultValue: 9,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 0;
        else return 9;
      },
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '分',
      key: 'timeMinute',
      comment: '分钟',
      minValue: 0,
      maxValue: 60,
      returnValue: '[0, 60)',
      canNotReachMaxValue: true,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '分',
      value: () => {
        const day = dayjs();
        const minute = Number(day.format('m'));
        const second = Number(day.format('s'));
        return minute + round(second / 60, 1);
      },
      defaultValue: 28,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 9;
        else return 28;
      },
      allowShapeTypes: [
        'image',
        'image-array-digit',
        'image-array-normal',
        'text',
        'progress-bar'
      ],
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image'].includes(shapeType);
            }
            case 'AOD': {
              return ['image'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '分十位',
      key: 'timeMinuteHigh',
      comment: '分钟的十位数',
      minValue: 0,
      maxValue: 5,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('mm').slice(0, 1)),
      defaultValue: 2,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 0;
        else return 2;
      },
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '分个位',
      key: 'timeMinuteLow',
      comment: '分钟的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('mm').slice(1)),
      defaultValue: 8,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 9;
        else return 8;
      },
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '秒',
      key: 'timeSecond',
      comment: '秒钟',
      minValue: 0,
      maxValue: 60,
      returnValue: '[0, 60)',
      canNotReachMaxValue: true,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '秒',
      allowShapeTypes: [
        'image',
        'image-array-digit',
        'image-array-normal',
        'text',
        'progress-bar'
      ],
      value: () => Number(dayjs().format('s')),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 24;
        else return 0;
      },
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (themeType == 'AOD') return false;
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '秒十位',
      key: 'timeSecondHigh',
      comment: '秒钟的十位数',
      minValue: 0,
      maxValue: 5,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('ss').slice(0, 1)),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 2;
        else return 0;
      },
      excludeDeviceTypes: ['M66'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      }
    },
    {
      label: '秒个位',
      key: 'timeSecondLow',
      comment: '秒钟的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('ss').slice(1)),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 4;
        else return 0;
      },
      excludeDeviceTypes: ['M66'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      }
    },
    {
      label: '年',
      key: 'dateYear',
      comment: '年',
      minValue: 0,
      maxValue: 9999,
      type: 'time',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '年',
      allowShapeTypes: ['image', 'image-array-digit', 'text', 'progress-bar'],
      value: () => Number(dayjs().format('YYYY')),
      defaultValue: _getYearValue(),
      lunarKey: 'dateLunarStringYear',
      excludeDeviceTypes: ['M66']
    },
    {
      label: '年千位',
      key: 'dateYearDigit4',
      comment: '年的千位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('YYYY').slice(0, 1)),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 2
    },
    {
      label: '年百位',
      key: 'dateYearDigit3',
      comment: '年的百位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('YYYY').slice(1, 2)),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 0
    },
    {
      label: '年十位',
      key: 'dateYearDigit2',
      comment: '年十位',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('YYYY').slice(2, 3)),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 2
    },
    {
      label: '年个位',
      key: 'dateYearDigit1',
      comment: '年的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('YYYY').slice(3)),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 2
    },
    {
      label: '月',
      key: 'dateMonth',
      comment: '月份',
      minValue: 1,
      maxValue: 12,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '月',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('M')),
      defaultValue: 8,
      lunarKey: 'dateLunarStringMonth',
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '月十位',
      key: 'dateMonthHigh',
      comment: '月份的十位数',
      minValue: 0,
      maxValue: 1,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('MM').slice(0, 1)),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 0
    },
    {
      label: '月个位',
      key: 'dateMonthLow',
      comment: '月份的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('MM').slice(1)),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 8
    },
    {
      label: '月日',
      key: 'date',
      comment: '月和日',
      minValue: 1,
      maxValue: 12,
      type: 'time',
      formats: [
        DataSourceFormat['Format_8/16'],
        DataSourceFormat['Format_08/16'],
        DataSourceFormat['Format_text_8/16'],
        DataSourceFormat.Format_8月16日,
        DataSourceFormat.Format_08月16日,
        DataSourceFormat.Format_text_8月16日
      ],
      formatsFn: (deviceType, shapeType) => {
        // O61, P62, P65开始支持
        if (
          ![
            'L61Simulator',
            'L61',
            'L67',
            'N60',
            'N65',
            'M67',
            'M62',
            'M65S',
            'M66',
            'N62',
            'N62S',
            'N65A',
            'N65B',
            'N65C',
            'N66',
            'N67',
            'O62',
            'O62M',
            'O63',
            'Q61',
            'Q63',
            'O65',
            'O65M',
            'O66',
            'Q66'
          ].includes(deviceType) &&
          shapeType == 'Text'
        ) {
          return [
            DataSourceFormat['Format_8/16'],
            DataSourceFormat['Format_08/16'],
            DataSourceFormat['Format_text_8/16'],
            DataSourceFormat.Format_8月16日,
            DataSourceFormat.Format_08月16日,
            DataSourceFormat.Format_text_8月16日
          ];
        } else {
          return [
            DataSourceFormat['Format_8/16'],
            DataSourceFormat['Format_08/16'],
            DataSourceFormat.Format_8月16日,
            DataSourceFormat.Format_08月16日
          ];
        }
      },
      returnValue: '[1月1日, 12月31日]',
      allowShapeTypes: ['text', 'image-array-digit'],
      value: () => Number(dayjs().format('M')),
      defaultValue: 8,
      widgetImageArray: 'date',
      excludeDeviceTypes: ['M66']
    },
    {
      label: '日',
      key: 'dateDay',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('D')),
      defaultValue: 16,
      lunarKey: 'dateLunarStringDay',
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return [''].includes(shapeType);
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '日十位',
      key: 'dateDayHigh',
      comment: '日的十位数',
      minValue: 0,
      maxValue: 3,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('DD').slice(0, 1)),
      defaultValue: 1,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '日个位',
      key: 'dateDayLow',
      comment: '日的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('DD').slice(1)),
      defaultValue: 6,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '昨天日期',
      key: 'miscdateYestarday',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().subtract(1, 'D').format('D')),
      defaultValue: 15,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '明天日期',
      key: 'miscdateTomorrow',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().add(1, 'D').format('D')),
      defaultValue: 17,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '前天日期',
      key: 'miscdateDayBeforeYestarday',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().subtract(1, 'D').format('D')),
      defaultValue: 14,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '大前天日期',
      key: 'miscdateDayThreeDaysAgo',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().subtract(1, 'D').format('D')),
      defaultValue: 13,
      includeDeviceTypes: [
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O66',
        'P67'
      ]
    },
    {
      label: '后天日期',
      key: 'miscdateDayAfterTomorrow',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().add(1, 'D').format('D')),
      defaultValue: 18,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '大后天日期',
      key: 'miscdateDayThreeDaysFromNow',
      comment: '日',
      minValue: 1,
      maxValue: 31,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '日',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().add(1, 'D').format('D')),
      defaultValue: 19,
      includeDeviceTypes: [
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '星期',
      key: 'dateWeek',
      comment: '星期',
      minValue: 0,
      maxValue: 6,
      labelRange: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => dayjs().day(),
      defaultValue: 0,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '昨天星期',
      key: 'miscdateYestardayWeak',
      comment: '昨天的星期',
      minValue: 0,
      maxValue: 6,
      labelRange: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => dayjs().day(),
      defaultValue: 6,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '明天星期',
      key: 'miscdateTomorrowWeak',
      comment: '明天的星期',
      minValue: 0,
      maxValue: 6,
      labelRange: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => dayjs().day(),
      defaultValue: 1,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '天干地支',
      key: 'dateSexagenaryCycle',
      comment: '0-59，分别代表：甲子、乙丑、…、壬戌、癸亥',
      allowShapeTypes: [
        'image',
        'text',
        'image-array-normal',
        'image-array-digit'
      ],
      labelRange: sexagenaryCycleArr,
      minValue: 0,
      maxValue: 59,
      returnValue: '[0,60)',
      type: 'time',
      defaultValue: sexagenaryCycleArr.findIndex((item) => item == '丙午'),
      includeDeviceTypes: [
        'N67',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '天干地支字符串',
      key: 'dateLunarStringYear',
      allowShapeTypes: ['text'],
      comment: '天干地支',
      formats: [DataSourceFormat.Format_甲子, DataSourceFormat.Format_甲子年],
      unit: '年',
      returnValue: '甲子、乙丑、…、壬戌、癸亥',
      type: 'time',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '农历月字符串',
      key: 'dateLunarStringMonth',
      allowShapeTypes: ['text'],
      comment: '',
      returnValue: '正月、二月、…、冬月、腊月',
      type: 'time',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '农历日字符串',
      key: 'dateLunarStringDay',
      allowShapeTypes: ['text'],
      comment: '',
      returnValue: '初一、初二、…、廿九、三十',
      type: 'time',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-时间',
      key: 'dualtime',
      comment: '目标时区的时分',
      type: 'time',
      minValue: 0,
      maxValue: 24,
      returnValue: '[00:00, 24:00)',
      canNotReachMaxValue: true,
      unit: ':',
      allowShapeTypes: ['image', 'image-array-digit', 'text', 'progress-bar'],
      formats: [
        DataSourceFormat['Format_8:00'],
        DataSourceFormat['Format_08:00']
      ],
      value: () => Number(dayjs().format('H')),
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      widgetImageArray: 'time'
    },
    {
      label: '双时区-小时',
      key: 'dualtimeHour',
      comment: '目标时区的小时',
      type: 'time',
      minValue: 0,
      maxValue: 24,
      returnValue: '[0, 24)',
      canNotReachMaxValue: true,
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      value: () => {
        const day = dayjs();
        const hour = Number(day.format('H'));
        const minute = Number(day.format('m'));
        return hour + round(minute / 60, 1);
      },
      defaultValue: 9,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 10;
        else return 9;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-小时十位',
      key: 'dualtimeHourHigh',
      comment: '目标时区的小时十位',
      minValue: 0,
      maxValue: 2,
      returnValue: '[0, 2]',
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('HH').slice(0, 1)),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 1;
        else return 0;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-小时个位',
      key: 'dualtimeHourLow',
      comment: '目标时区的小时个位',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('HH').slice(1)),
      defaultValue: 9,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 0;
        else return 9;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-分',
      key: 'dualtimeMinute',
      comment: '目标时区的分钟',
      minValue: 0,
      maxValue: 60,
      returnValue: '[0, 60)',
      canNotReachMaxValue: true,
      type: 'time',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '分',
      allowShapeTypes: ['image', 'image-array-digit', 'text', 'progress-bar'],
      value: () => {
        const day = dayjs();
        const minute = Number(day.format('m'));
        const second = Number(day.format('s'));
        return minute + round(second / 60, 1);
      },
      defaultValue: 28,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 9;
        else return 28;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-分十位',
      key: 'dualtimeMinuteHigh',
      comment: '目标时区的分钟十位',
      minValue: 0,
      maxValue: 5,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('mm').slice(0, 1)),
      defaultValue: 2,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 0;
        else return 2;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-分个位',
      key: 'dualtimeMinuteLow',
      comment: '目标时区的分钟个位',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      allowShapeTypes: ['*'],
      value: () => Number(dayjs().format('mm').slice(1)),
      defaultValue: 8,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 9;
        else return 8;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-秒',
      key: 'dualtimeSecond',
      comment: '目标时区的秒钟',
      minValue: 0,
      maxValue: 60,
      returnValue: '[0, 60)',
      canNotReachMaxValue: true,
      type: 'time',
      allowShapeTypes: ['image', 'image-array-digit', 'text', 'progress-bar'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      },
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '秒',
      value: () => Number(dayjs().format('s')),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 24;
        else return 0;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-秒十位',
      key: 'dualtimeSecondHigh',
      comment: '目标时区的秒钟十位',
      minValue: 0,
      maxValue: 5,
      type: 'time',
      value: () => Number(dayjs().format('ss').slice(0, 1)),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 2;
        else return 0;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O62M',
        'N62',
        'O63',
        'Q61',
        'Q63',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      allowShapeTypes: ['*'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      }
    },
    {
      label: '双时区-秒个位',
      key: 'dualtimeSecondLow',
      comment: '目标时区的秒钟个位',
      minValue: 0,
      maxValue: 9,
      type: 'time',
      value: () => Number(dayjs().format('ss').slice(1)),
      defaultValue: 0,
      defaultValueFn: (deviceType) => {
        if (canUse('default-time-change', deviceType).valid) return 4;
        else return 0;
      },
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      allowShapeTypes: ['*'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      }
    },
    {
      label: '双时区-AM/PM',
      key: 'dualtimeIsPM',
      comment: '目标时区，0：上午；1：下午',
      minValue: 0,
      maxValue: 1,
      labelRange: ['上午', '下午'],
      type: 'time',
      allowShapeTypes: ['image', 'image-array-normal', 'text', 'progress-bar'],
      value: () => (dayjs().format('A') == 'PM' ? 1 : 0),
      defaultValue: 0,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-城市列表',
      key: 'dualtimeCity',
      comment: '0：格林威治、1：惠灵顿、2：悉尼...',
      minValue: 0,
      maxValue: 42,
      labelRange: dualtimeCityList.map((item) => item.name),
      type: 'time',
      allowShapeTypes: ['image', 'image-array-normal', 'text', 'progress-bar'],
      value: () => {
        return random(0, 42);
      },
      defaultValue: 0,
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'N62',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '双时区-城市列表字符串',
      key: 'dualtimeStringCity',
      allowShapeTypes: ['text'],
      comment: '',
      returnValue: 'GMT、WLG、SYD、...、LAX',
      type: 'time',
      includeDeviceTypes: [
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O66',
        'P67',
        'Q66'
      ]
    },
    {
      label: '农历年',
      key: 'dateLunarYear',
      comment: '年',
      minValue: 0,
      maxValue: 9999,
      type: 'time',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '年',
      allowShapeTypes: ['image', 'image-array-digit', 'text', 'progress-bar'],
      value: () => Number(dayjs().format('YYYY')),
      excludeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'M62',
        'M65S',
        'M66',
        'M67',
        'N66',
        'N67',
        'O62',
        'O65',
        'O65M',
        'N62'
      ],
      defaultValue: 2026
    },
    {
      label: '农历月',
      key: 'dateLunarMonth',
      comment: '农历月',
      minValue: 1,
      maxValue: 12,
      allowShapeTypes: ['*'],
      type: 'time',
      excludeDeviceTypes: ['N60', 'M66', 'N65A', 'N65B', 'N65C'],
      defaultValue: 7
    },
    // {
    //   label: '农历月十位',
    //   key: 'dateLunarMonthHigh',
    //   comment: '农历月的十位数',
    //   minValue: 0,
    //   maxValue: 1,
    //   type: 'time',
    //   allowShapeTypes: ['*'],
    //   value: () => Number(dayjs().format('MM').slice(0, 1)),
    //   excludeDeviceTypes: ['M67', 'N67'],
    //   defaultValue: 0
    // },
    // {
    //   label: '农历月个位',
    //   key: 'dateLunarMonthLow',
    //   comment: '农历月的个位数',
    //   minValue: 0,
    //   maxValue: 9,
    //   type: 'time',
    //   allowShapeTypes: ['*'],
    //   value: () => Number(dayjs().format('MM').slice(1)),
    //   excludeDeviceTypes: ['M67', 'N67'],
    //   defaultValue: 8
    // },
    {
      label: '农历日',
      key: 'dateLunarDay',
      comment: '农历日',
      minValue: 1,
      maxValue: 30,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar', 'text'],
      labelRange: _getLunarDayLabels(),
      defaultValue: 4,
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '今日月相',
      key: 'dateMoon',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 1,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '明日月相',
      key: 'dateMoonTomorrow',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 1,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '后日月相',
      key: 'dateMoonAfterTomorrow',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 1,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '3天后月相',
      key: 'dateMoonThreeTomorrow',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 2,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '4天后月相',
      key: 'dateMoonFourDaysAgo',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 2,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '5天后月相',
      key: 'dateMoonFiveDaysAgo',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 3,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '6天后月相',
      key: 'dateMoonSixDaysAgo',
      comment:
        '新月=0，蛾眉月=1，上弦月=2，盈凸月=3，满月=4，亏凸月=5，下弦月=6，残月=7',
      minValue: 0,
      maxValue: 7,
      type: 'time',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 3,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '中文星期字符串1',
      key: 'dateWeekStringShortCN',
      returnValue: '周一、周二、…、周六、周日',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({
        cn: true,
        cnFlag: '周'
      }),
      value: () =>
        _getDateWeekLabels({
          cn: true,
          cnFlag: '周'
        })?.[dayjs().day()],
      defaultValue: '周日'
    },
    {
      label: '中文星期字符串2',
      key: 'dateWeekStringFullCN',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      returnValue: '星期一、星期二、…、星期六、星期日',
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({
        cn: true
      }),
      value: () =>
        _getDateWeekLabels({
          cn: true
        })?.[dayjs().day()],
      defaultValue: '星期日'
    },
    {
      label: '英文星期字符串1',
      key: 'dateWeekStringFullPascalEN',
      returnValue: 'Monday、Tuesday、…、Saturday、Sunday',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({}),
      value: () => _getDateWeekLabels({})?.[dayjs().day()],
      defaultValue: 'Sunday'
    },
    {
      label: '英文星期字符串2',
      key: 'dateWeekStringFullUpperEN',
      returnValue: 'MONDAY、TUESDAY、…、SATURDAY、SUNDAY',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({ _case: 'upper' }),
      value: () => _getDateWeekLabels({ _case: 'upper' })?.[dayjs().day()],
      defaultValue: 'SUNDAY'
    },
    {
      label: '英文星期字符串3',
      key: 'dateWeekStringFullLowerEN',
      returnValue: 'monday、tuesday、…、saturday、sunday',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({ _case: 'lower' }),
      value: () => _getDateWeekLabels({ _case: 'lower' })?.[dayjs().day()],
      defaultValue: 'sunday'
    },
    {
      label: '英文星期字符串4',
      key: 'dateWeekStringShortPascalEN',
      returnValue: 'Mon、Tue、…、Sat、Sun',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({ format: 'ddd' }),
      value: () => _getDateWeekLabels({ format: 'ddd' })?.[dayjs().day()],
      defaultValue: 'Sun'
    },
    {
      label: '英文星期字符串5',
      key: 'dateWeekStringShortUpperEN',
      returnValue: 'MON、TUE、…、SAT、SUN',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({ format: 'ddd', _case: 'upper' }),
      value: () =>
        _getDateWeekLabels({ format: 'ddd', _case: 'upper' })?.[dayjs().day()],
      defaultValue: 'SUN'
    },
    {
      label: '英文星期字符串6',
      key: 'dateWeekStringShortLowerEN',
      returnValue: 'mon、tue、…、sat、sun',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateWeekLabels({ format: 'ddd', _case: 'lower' }),
      value: () =>
        _getDateWeekLabels({ format: 'ddd', _case: 'lower' })?.[dayjs().day()],
      defaultValue: 'sun'
    },
    {
      label: '中文月份字符串1',
      key: 'dateMonthStringShortCN',
      returnValue: '一月、二月、…、十二月',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({ cn: true }),
      value: () => _getDateMonthLabels({ cn: true })?.[dayjs().month()],
      defaultValue: '八月'
    },
    {
      label: '英文月份字符串1',
      key: 'dateMonthStringFullPascalEN',
      returnValue: 'January、February、…、December',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({}),
      value: () => _getDateMonthLabels({})?.[dayjs().month()],
      defaultValue: 'August'
    },
    {
      label: '英文月份字符串2',
      key: 'dateMonthStringFullUpperEN',
      returnValue: 'JANUARY、FEBRUARY、…、DECEMBER',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({ _case: 'upper' }),
      value: () => _getDateMonthLabels({ _case: 'upper' })?.[dayjs().month()],
      defaultValue: 'AUGUST'
    },
    {
      label: '英文月份字符串3',
      key: 'dateMonthStringFullLowerEN',
      returnValue: 'january、february、…、december',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({ _case: 'lower' }),
      value: () => _getDateMonthLabels({ _case: 'lower' })?.[dayjs().month()],
      defaultValue: 'august'
    },
    {
      label: '英文月份字符串4',
      key: 'dateMonthStringShortPascalEN',
      returnValue: 'Jan、Feb、…、Dec',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({ format: 'MMM' }),
      value: () => _getDateMonthLabels({ format: 'MMM' })?.[dayjs().month()],
      defaultValue: 'Aug'
    },
    {
      label: '英文月份字符串5',
      key: 'dateMonthStringShortUpperEN',
      returnValue: 'JAN、FEB、…、DEC',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({ format: 'MMM', _case: 'upper' }),
      value: () =>
        _getDateMonthLabels({ format: 'MMM', _case: 'upper' })?.[
        dayjs().month()
        ],
      defaultValue: 'AUG'
    },
    {
      label: '英文月份字符串6',
      key: 'dateMonthStringShortLowerEN',
      returnValue: 'jan、feb、…、dec',
      includeDeviceTypes: [
        'N65A',
        'N65B',
        'N67',
        'N66',
        'N62S',
        'O61',
        'P65',
        'P62',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'O65',
        'O65M',
        'O66',
        'P67',
        'Q66'
      ],
      type: 'time',
      allowShapeTypes: ['text'],
      labelRange: _getDateMonthLabels({ format: 'MMM', _case: 'lower' }),
      value: () =>
        _getDateMonthLabels({ format: 'MMM', _case: 'lower' })?.[
        dayjs().month()
        ],
      defaultValue: 'aug'
    },
    {
      label: 'AM/PM',
      key: 'miscIsPM',
      comment: '0：上午；1：下午',
      minValue: 0,
      maxValue: 1,
      labelRange: ['上午', '下午'],
      type: 'time',
      allowShapeTypes: ['image', 'image-array-normal', 'text', 'progress-bar'],
      excludeDeviceTypes: ['M62', 'N65A', 'N65B', 'N65C'],
      value: () => (dayjs().format('A') == 'PM' ? 1 : 0),
      defaultValue: 0,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal'].includes(shapeType);
            }
            case 'AOD': {
              return ['image-array-normal'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '时间段',
      key: 'miscTimeSection',
      comment:
        '0：凌晨，1：黎明，2：清晨，3：上午，4：中午，5：下午，6：傍晚，7：黄昏，8：晚上',
      minValue: 0,
      maxValue: 8,
      labelRange: [
        '凌晨',
        '黎明',
        '清晨',
        '上午',
        '中午',
        '下午',
        '傍晚',
        '黄昏',
        '晚上'
      ],
      type: 'time',
      allowShapeTypes: ['image', 'image-array-normal', 'text', 'progress-bar'],
      value: () => {
        return random(0, 8);
      },
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C', 'N60', 'M62', 'M65S', 'M66'],
      defaultValue: 3
    },
    {
      label: '24H',
      key: 'miscIs24H',
      comment: '当前时间是否为24h',
      minValue: 0,
      maxValue: 1,
      labelRange: ['否', '是'],
      type: 'time',
      allowShapeTypes: ['image', 'image-array-normal', 'text', 'progress-bar'],
      value: () => random(0, 1),
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 1
    }
  ],
  运动健康: [
    {
      label: '步数',
      key: 'healthStepCount',
      comment: '步数',
      minValue: 0,
      maxValue: 99999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '步',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 99999),
      jumpAppName: 'activities',
      defaultValue: 2560,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-digit'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '步数万位',
      key: 'healthStepCountDigit5',
      comment: '步数的万位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['*'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 0
    },
    {
      label: '步数千位',
      key: 'healthStepCountDigit4',
      comment: '步数的千位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['*'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 2
    },
    {
      label: '步数百位',
      key: 'healthStepCountDigit3',
      comment: '步数的百位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['*'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 5
    },
    {
      label: '步数十位',
      key: 'healthStepCountDigit2',
      comment: '步数的十位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['*'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 6
    },
    {
      label: '步数个位',
      key: 'healthStepCountDigit1',
      comment: '步数的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['*'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 0
    },
    {
      label: '步数完成进度',
      key: 'healthStepProgress',
      comment: '步数完成进度',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      progressAccuracy: [10, 5, 0],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'activities',
      defaultValue: 80,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '步数目标',
      key: 'healthStepTarget',
      comment: '步数目标值',
      minValue: 0,
      maxValue: 99999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '步',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 99999),
      jumpAppName: 'activities',
      defaultValue: 3200,
      excludeDeviceTypes: [
        'N65',
        'M67',
        'N60',
        'M62',
        'M65S',
        'M66',
        'N65A',
        'N65B',
        'N65C'
      ]
    },
    {
      label: '步数距离',
      key: 'healthStepKiloMeter',
      comment: '单位km',
      minValue: 0,
      maxValue: 100,
      precision: 1,
      type: 'health',
      formats: [
        DataSourceFormat['Format_8.0'],
        DataSourceFormat['Format_8.0时']
      ],
      unit: 'km',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 100, true),
      jumpAppName: 'activities',
      defaultValue: 1.6,
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C', 'M66']
    },
    {
      label: '卡路里',
      key: 'healthCalorieValue',
      comment: '今日消耗卡路里',
      minValue: 0,
      maxValue: 99999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'kcal',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 99999),
      jumpAppName: 'activities',
      defaultValue: 162,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-digit'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '卡路里万位',
      key: 'healthCalorieValueDigit5',
      comment: '今日消耗卡路里的万位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 0
    },
    {
      label: '卡路里千位',
      key: 'healthCalorieValueDigit4',
      comment: '今日消耗卡路里的千位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 0
    },
    {
      label: '卡路里百位',
      key: 'healthCalorieValueDigit3',
      comment: '今日消耗卡路里的百位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 1
    },
    {
      label: '卡路里十位',
      key: 'healthCalorieValueDigit2',
      comment: '今日消耗卡路里的十位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 6
    },
    {
      label: '卡路里个位',
      key: 'healthCalorieValueDigit1',
      comment: '今日消耗卡路里的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 2
    },
    {
      label: '卡路里完成度',
      key: 'healthCalorieProgress',
      comment: '卡路里完成进度',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      progressAccuracy: [10, 5, 0],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'activities',
      defaultValue: 80,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '卡路里目标',
      key: 'healthCalorieTarget',
      comment: '卡路里目标值',
      minValue: 0,
      maxValue: 99999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'kcal',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 99999),
      jumpAppName: 'activities',
      defaultValue: 203,
      excludeDeviceTypes: ['N65', 'M67', 'N60', 'M62', 'M65S', 'M66']
    },
    {
      label: '站立次数',
      key: 'healthStandCount',
      comment: '站立次数',
      minValue: 0,
      maxValue: 24,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '次',
      allowShapeTypes: ['*'],
      value: () => random(0, 24),
      jumpAppName: 'activities',
      defaultValue: 8,
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C'],
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '站立次数十位',
      key: 'healthStandCountDigit2',
      comment: '站立次数的十位数',
      minValue: 0,
      maxValue: 2,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 2),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 0
    },
    {
      label: '站立次数个位',
      key: 'healthStandCountDigit1',
      comment: '站立次数的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 8
    },
    {
      label: '站立完成进度',
      key: 'healthStandProgress',
      comment: '站立完成进度',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      progressAccuracy: [10, 5, 0],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'activities',
      defaultValue: 80,
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C'],
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '站立目标',
      key: 'healthStandTarget',
      comment: '站立次数目标',
      minValue: 0,
      maxValue: 12,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '次',
      allowShapeTypes: ['*'],
      value: () => random(0, 12),
      jumpAppName: 'activities',
      defaultValue: 10,
      excludeDeviceTypes: ['N65', 'M67', 'N60', 'M62', 'M65S', 'M66']
    },
    {
      label: '中高强度锻炼时长',
      key: 'healthExerciseDuration',
      comment: '单位分钟',
      minValue: 0,
      maxValue: 999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '分钟',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 999),
      jumpAppName: 'activities',
      defaultValue: 24,
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '中高强度锻炼时长百位',
      key: 'healthExerciseDurationDigit3',
      comment: '中高强度锻炼时长的百位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 0
    },
    {
      label: '中高强度锻炼时长十位',
      key: 'healthExerciseDurationDigit2',
      comment: '中高强度锻炼时长的十位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 2
    },
    {
      label: '中高强度锻炼时长个位',
      key: 'healthExerciseDurationDigit1',
      comment: '中高强度锻炼时长的个位数',
      minValue: 0,
      maxValue: 9,
      type: 'health',
      allowShapeTypes: ['image-array-digit'],
      value: () => random(0, 9),
      jumpAppName: 'activities',
      includeDeviceTypes: [
        'O62M',
        'O63',
        'Q61',
        'Q63',
        'P65',
        'P62',
        'P67',
        'Q66'
      ],
      defaultValue: 4
    },
    {
      label: '中高强度锻炼完成度',
      key: 'healthExerciseProgress',
      comment: '',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      progressAccuracy: [10, 5, 0],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'activities',
      defaultValue: 80,
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '中高强度目标',
      key: 'healthExerciseTarget',
      comment: '',
      minValue: 0,
      maxValue: 999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '分钟',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 999),
      jumpAppName: 'activities',
      defaultValue: 30,
      excludeDeviceTypes: [
        'N65',
        'M67',
        'N60',
        'M62',
        'M65S',
        'M66',
        'N65A',
        'N65B',
        'N65C'
      ]
    },
    {
      label: '心率',
      key: 'healthHeartRate',
      comment: '心率，单位bpm',
      minValue: 0,
      maxValue: 999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'bpm',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 999),
      jumpAppName: 'heartrate',
      defaultValue: 78,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-digit'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      },
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '心率区间',
      key: 'healthHeartRateZone',
      comment: '0-6，分别代表：安静、热身、燃脂、有氧、无氧、极限，无效',
      minValue: 0,
      maxValue: 6,
      labelRange: ['安静', '热身', '燃脂', '有氧', '无氧', '极限', '无效'],
      type: 'health',
      allowShapeTypes: ['image', 'image-array-normal', 'text', 'progress-bar'],
      value: () => random(0, 6),
      jumpAppName: 'heartrate',
      defaultValue: 3,
      excludeDeviceTypes: ['M66']
    },
    {
      label: '心率最低值',
      key: 'healthHeartRateMin',
      comment: '今日心率最低值',
      minValue: 0,
      maxValue: 999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'bpm',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 999),
      jumpAppName: 'heartrate',
      defaultValue: 60,
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '心率最高值',
      key: 'healthHeartRateMax',
      comment: '今日心率最高值',
      minValue: 0,
      maxValue: 999,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'bpm',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 999),
      jumpAppName: 'heartrate',
      defaultValue: 100,
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '血氧',
      key: 'healthOxygenSpO2',
      comment: '血氧，单位%',
      minValue: 0,
      maxValue: 100,
      progressAccuracy: [10, 5, 0],
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'SpO2',
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      defaultValue: 98,
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '压力指数',
      key: 'healthPressureIndex',
      comment: '压力指数，0~100',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      progressAccuracy: [10, 5, 0],
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'pressure',
      defaultValue: 20,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-digit'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      },
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '睡眠时长',
      key: 'healthSleepDuration',
      comment: '单位小时，会有小数点',
      minValue: 0,
      maxValue: 24,
      type: 'health',
      precision: 1,
      formats: [
        DataSourceFormat['Format_8.0'],
        DataSourceFormat['Format_8.0时']
      ],
      unit: '小时',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => Number(random(0, 24, true).toFixed(1)),
      jumpAppName: 'sleep',
      defaultValue: 7.2,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66']
    },
    {
      label: '睡眠目标时长',
      key: 'healthSleepTarget',
      comment: '单位小时，会有小数点',
      minValue: 0,
      maxValue: 24,
      type: 'health',
      precision: 1,
      formats: [
        DataSourceFormat['Format_8.0'],
        DataSourceFormat['Format_8.0时']
      ],
      unit: '小时',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => Number(random(0, 24, true).toFixed(1)),
      jumpAppName: 'sleep',
      defaultValue: 9.0,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: [
        'N65',
        'M67',
        'N60',
        'M62',
        'M65S',
        'M66',
        'N65A',
        'N65B',
        'N65C'
      ]
    },
    {
      label: '睡眠质量',
      key: 'healthSleepQuality',
      comment: '0: 未知 1:有待提高 2:一般 3:良好 4:优',
      minValue: 0,
      maxValue: 4,
      type: 'health',
      allowShapeTypes: ['image-array-normal'],
      jumpAppName: 'sleep',
      defaultValue: 3,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ['O61', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '睡眠目标完成度',
      key: 'healthSleepTargetProgress',
      comment: '',
      minValue: 0,
      maxValue: 100,
      progressAccuracy: [10, 5, 0],
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'sleep',
      defaultValue: 80,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        if (fillType == '数字填充') return true;
        else
          return ['O61', 'P62', 'P65', 'P67', 'Q61'].indexOf(deviceType) !== -1;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '睡眠HRV',
      key: 'healthSleepHRV',
      comment: '0：无效，1：较低，2：正常，3：较高',
      minValue: 0,
      maxValue: 3,
      type: 'health',
      allowShapeTypes: ['image-array-normal', 'image', 'text', 'image-array-digit'],
      jumpAppName: 'sleep',
      defaultValue: 2,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        return true
      },
      includeDeviceTypes: ElectronApi.isProfessional()?['Q61', 'P67', 'P62', 'Q63', 'Q66']:['P62', 'P67']
    },
    {
      label: '体能状态',
      key: 'appSportTrainingStatus',
      comment: '无状态=0，体能丧失=1，维持体能=2，有效训练=3，训练过度=4',
      minValue: 0,
      maxValue: 4,
      type: 'health',
      allowShapeTypes: ['image-array-normal', 'image'],
      defaultValue: 2,
      includeDeviceTypes: ['O61', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '本周训练负荷',
      key: 'appSportTrainingLoad',
      comment: '0：无效，1：低于推荐，2：符合推荐，3：超出推荐',
      minValue: 0,
      maxValue: 3,
      type: 'health',
      allowShapeTypes: ['image-array-normal', 'image'],
      defaultValue: 2,
      includeDeviceTypes: ['O61', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '恢复时间',
      key: 'appSportRecoveryTime',
      comment: '单位小时',
      minValue: 0,
      maxValue: 96,
      precision: 1,
      formats: [
        DataSourceFormat['Format_8.0'],
        DataSourceFormat['Format_8.0时']
      ],
      unit: '小时',
      type: 'health',
      allowShapeTypes: ['image', 'image-array-digit'],
      value: () => random(0, 96),
      jumpAppName: 'trainingStatus',
      defaultValue: 5,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '体力恢复',
      key: 'appSportRecoveryPercentage',
      comment: '百分比，表示用户体力恢复程度',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      allowShapeTypes: ['image-array-digit', 'image', 'text', 'progress-bar'],
      defaultValue: 90,
      needEmptyValue: () => {
        return false
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['Q61', 'P62', 'Q63', 'O61', 'Q66'] : ['P62', 'O61'],
    },
    {
      label: '跑力指数',
      key: 'appSportRunPowerIndex',
      comment:
        '无数据=0，新手=1，初学者=2，运动爱好者=3，进阶运动者=4，菁英选手=5',
      minValue: 0,
      maxValue: 5,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar'],
      jumpAppName: 'trainingStatus',
      defaultValue: 3,
      includeDeviceTypes: ['O61', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '最大摄氧量',
      key: 'appSportOxygenUpdateMax',
      comment:
        '无数据=0，新手=1，一般=2，入门=3，中等=4，良好=5，优秀=6，超群=7',
      minValue: 0,
      maxValue: 7,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar'],
      defaultValue: 5,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '今日最高压力',
      key: 'healthPressureMax',
      comment: '今日最高压力，0～100',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-digit', 'text'],
      defaultValue: 50,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '今日最低压力',
      key: 'healthPressureMin',
      comment: '今日最低压力，0～100',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-digit', 'text'],
      defaultValue: 10,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '今日最高血氧',
      key: 'healthOxygenSpO2Max',
      comment: '今日最高血氧，0～100%',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-digit', 'text'],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      defaultValue: 100,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '今日最低血氧',
      key: 'healthOxygenSpO2Min',
      comment: '今日最低血氧，0～100%',
      minValue: 0,
      maxValue: 100,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-digit', 'text'],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      defaultValue: 95,
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66'],
      needEmptyValue: () => {
        return true;
      }
    },

    {
      label: '女性距离下一经期时间',
      key: 'healthMenstrualCountdown',
      comment: '女性距离下一个经期时间，0～90天',
      minValue: 0,
      maxValue: 90,
      type: 'health',
      allowShapeTypes: ['image', 'image-array-digit', 'text'],
      allowShapeTypeFn: ({ shapeType, deviceType }) => {
        // 文本类型仅 Q 代产品支持
        if (shapeType === 'text') {
          return ['Q61', 'Q63', 'Q66'].includes(deviceType);
        }
        return true;
      },
      defaultValue: 10,
      includeDeviceTypes: ['O61', 'P62', 'Q61', 'Q63', 'Q66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '血压舒张压',
      key: 'healthBloodDiastolicPressureMmhg_press',
      comment: '单位mmHg或kPa（跟随系统设置）',
      type: 'health',
      tag: 'compound',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      minValue: 0,
      maxValue: 300,
      returnValue: '[0, 300]',
      children: [
        'healthBloodDiastolicPressureMmhg',
        'healthBloodDiastolicPressureKpa'
      ],
      unit: 'mmHg/kPa',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'bloodPressure',
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },
    {
      label: '血压舒张压（mmHg）',
      key: 'healthBloodDiastolicPressureMmhg',
      comment: '单位mmHg ',
      minValue: 0,
      maxValue: 300,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'mmHg',
      tag: 'subunit_1',
      value: () => random(0, 300),
      jumpAppName: 'bloodPressure',
      defaultValue: 60,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },
    {
      label: '血压舒张压（kPa）',
      key: 'healthBloodDiastolicPressureKpa',
      comment: '单位kPa',
      minValue: 0,
      maxValue: 40,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'kPa',
      precision: 1,
      tag: 'subunit_2',
      value: () => random(0, 40),
      jumpAppName: 'bloodPressure',
      defaultValue: 8.0,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },
    {
      label: '血压收缩压',
      key: 'healthBloodSystolicPressureMmhg2_press',
      comment: '单位mmHg或kPa（跟随系统设置）',
      type: 'health',
      tag: 'compound',
      minValue: 0,
      maxValue: 300,
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      returnValue: '[0, 300]',
      children: [
        'healthBloodSystolicPressureMmhg',
        'healthBloodSystolicPressureKpa'
      ],
      unit: 'mmHg/kPa',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'bloodPressure',
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },

    {
      label: '血压收缩压（mmHg）',
      key: 'healthBloodSystolicPressureMmhg',
      comment: '单位mmHg ',
      minValue: 0,
      maxValue: 300,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'mmHg',
      tag: 'subunit_1',
      value: () => random(0, 300),
      jumpAppName: 'bloodPressure',
      defaultValue: 120,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },

    {
      label: '血压收缩压（kPa）',
      key: 'healthBloodSystolicPressureKpa',
      comment: '单位kPa',
      minValue: 0,
      maxValue: 40,
      precision: 1,
      type: 'health',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'kPa',
      tag: 'subunit_2',
      value: () => random(0, 40),
      jumpAppName: 'bloodPressure',
      defaultValue: 16,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },
    {
      label: '血压单位',
      key: 'healthBloodPressureUnit',
      comment: '0：mmHg，1：kPa',
      minValue: 0,
      maxValue: 1,
      labelRange: ['mmHg', 'kPa'],
      type: 'health',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      defaultValue: 0,
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },
    {
      label: '血糖值',
      key: 'healthBloodSugarValue',
      comment: '0～25.0 单位mmol/L',
      minValue: 0,
      maxValue: 25.0,
      returnValue: '[0, 25.0]',
      precision: 1,
      type: 'health',
      formats: [
        DataSourceFormat['Format_8.0'],
        DataSourceFormat['Format_8.0时']
      ],
      unit: 'mmol/L',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => Number(random(0, 25, true).toFixed(1)),
      defaultValue: 6.1,
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },
    {
      label: '血糖更新时间',
      key: 'healthBloodSugarUpdateTsString',
      returnValue: '血糖更新时间',
      type: 'health',
      allowShapeTypes: ['text'],
      value: () => dayjs().format('YYYY-MM-DD HH:mm:ss'),
      defaultValue: '5分钟',
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N60'] : []
    },

  ],
  工具类: [
    {
      label: '日出时间',
      key: 'weatherCurrentSunRise',
      comment: '日出时间（跟随系统24H/12H设置）',
      type: 'system',
      formats: [
        DataSourceFormat['Format_8:00'],
        DataSourceFormat['Format_08:00']
      ],
      unit: ':',
      minValue: 0,
      maxValue: 24,
      returnValue: '[00:00, 24:00)',
      canNotReachMaxValue: true,
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      defaultValue: 6.017,
      excludeDeviceTypes: ['M67', 'N65', 'M66', 'N65A', 'N65B', 'N65C', 'N60'],
      needEmptyValue: () => {
        return true;
      },
      widgetImageArray: 'time'
    },
    {
      label: '日落时间',
      key: 'weatherCurrentSunSet',
      comment: '日落时间（跟随系统24H/12H设置）',
      type: 'system',
      minValue: 0,
      maxValue: 24,
      returnValue: '[00:00, 24:00)',
      canNotReachMaxValue: true,
      formats: [
        DataSourceFormat['Format_8:00'],
        DataSourceFormat['Format_08:00']
      ],
      unit: ':',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      excludeDeviceTypes: ['M67', 'N65', 'M66', 'N65A', 'N65B', 'N65C', 'N60'],
      defaultValue: 19.3,
      needEmptyValue: () => {
        return true;
      },
      widgetImageArray: 'time'
    },
    {
      label: '日出时间小时',
      key: 'weatherCurrentSunRiseHour',
      comment: '日出时间小时部分（跟随系统24H/12H设置）',
      minValue: 0,
      maxValue: 23,
      type: 'system',
      returnValue: '[0, 23]',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      excludeDeviceTypes: ['M67', 'N65', 'M66', 'N65A', 'N65B', 'N65C', 'N60'],
      defaultValue: 6,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        if (fillType == '数字填充') return true;
        else
          return ['O61', 'P62', 'P65', 'P67', 'Q61'].indexOf(deviceType) !== -1;
      }
    },
    {
      label: '日出时间分钟',
      key: 'weatherCurrentSunRiseMinute',
      comment: '日出时间分钟部分',
      minValue: 0,
      maxValue: 59,
      type: 'system',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '分',
      allowShapeTypes: ['*'],
      excludeDeviceTypes: ['M67', 'N65', 'M66', 'N65A', 'N65B', 'N65C', 'N60'],
      defaultValue: 1,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        if (fillType == '数字填充') return true;
        else
          return ['O61', 'P62', 'P65', 'P67', 'Q61'].indexOf(deviceType) !== -1;
      }
    },
    {
      label: '日落时间小时',
      key: 'weatherCurrentSunSetHour',
      comment: '日落时间小时部分（跟随系统24H/12H设置）',
      minValue: 0,
      maxValue: 23,
      returnValue: '[0, 23]',
      type: 'system',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      allowShapeTypes: ['*'],
      excludeDeviceTypes: ['M67', 'N65', 'M66', 'N65A', 'N65B', 'N65C', 'N60'],
      defaultValue: 19,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        if (fillType == '数字填充') return true;
        else
          return ['O61', 'P62', 'P65', 'P67', 'Q61'].indexOf(deviceType) !== -1;
      }
    },
    {
      label: '日落时间分钟',
      key: 'weatherCurrentSunSetMinute',
      comment: '日落时间分钟部分',
      minValue: 0,
      maxValue: 59,
      type: 'system',
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '分',
      allowShapeTypes: ['*'],
      excludeDeviceTypes: ['M67', 'N65', 'M66', 'N65A', 'N65B', 'N65C', 'N60'],
      defaultValue: 18,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        if (fillType == '数字填充') return true;
        else
          return ['O61', 'P62', 'P65', 'P67', 'Q61'].indexOf(deviceType) !== -1;
      }
    },
    {
      label: '温度单位',
      key: 'weatherTemperatureUnit',
      comment: '0：℃，1：℉',
      minValue: 0,
      maxValue: 1,
      labelRange: ['摄氏度', '华氏度'],
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      type: 'system',
      defaultValue: 0,
      excludeDeviceTypes: ['N62', 'M66', 'N65A', 'N65B', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '温度',
      key: 'weatherCurrentTempature_temp',
      comment: '单位摄氏度或华氏度（跟随系统设置）',
      type: 'system',
      tag: 'compound',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      returnValue: '[-99, 99]',
      children: [
        'weatherCurrentTemperature',
        'weatherCurrentTemperatureFahrenheit'
      ],
      unit: '℃/℉',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'weather',
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C'],
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-digit'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '温度（摄氏度）',
      key: 'weatherCurrentTemperature',
      comment: '单位摄氏度',
      minValue: -99,
      maxValue: 99,
      type: 'system',
      tag: 'subunit_1',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℃',
      value: () => Number(random(-99, 99, true).toFixed(0)),
      jumpAppName: 'weather',
      defaultValue: 25,
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '温度（华氏度）',
      key: 'weatherCurrentTemperatureFahrenheit',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      tag: 'subunit_2',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => Number(random(-147, 211, true).toFixed(0)),
      jumpAppName: 'weather',
      defaultValue: 77,
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C'],
      needEmptyValue: () => {
        return true;
      }
    },
    // 特殊开放数据源
    {
      label: '温度（华氏度）',
      key: 'weatherCurrentTemperatureFahrenheit_temp',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => Number(random(-147, 211, true).toFixed(0)),
      jumpAppName: 'weather',
      defaultValue: 77,
      includeDeviceTypes: ['P62', 'P65', 'P67', 'Q61', 'Q63', 'Q66'],
      allowShapeTypes: ['text'],
      onlyUseInPro: true
    },
    {
      label: '今天最高温度',
      key: 'weatherTodayTemperatureMax_temp',
      comment: '单位摄氏度或华氏度（跟随系统设置）',
      type: 'system',
      tag: 'compound',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      returnValue: '[-99, 99]',
      children: [
        'weatherTodayTemperatureMax',
        'weatherTodayTemperatureMaxFahrenheit'
      ],
      unit: '℃/℉',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'weather',
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '今天最高温度（摄氏度）',
      key: 'weatherTodayTemperatureMax',
      comment: '单位摄氏度',
      minValue: -99,
      maxValue: 99,
      type: 'system',
      tag: 'subunit_1',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℃',
      value: () => random(-99, 99),
      jumpAppName: 'weather',
      defaultValue: 28,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '今天最高温度（华氏度）',
      key: 'weatherTodayTemperatureMaxFahrenheit',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      tag: 'subunit_2',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => random(-147, 211),
      jumpAppName: 'weather',
      defaultValue: 82,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    // 特殊开放数据源
    {
      label: '今天最高温度（华氏度）',
      key: 'weatherTodayTemperatureMaxFahrenheit_temp',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => random(-147, 211),
      jumpAppName: 'weather',
      defaultValue: 82,
      allowShapeTypes: ['text'],
      onlyUseInPro: true,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ['P62', 'P65', 'P67', 'Q61', 'Q63', 'Q66']
    },
    {
      label: '今天最低温度',
      key: 'weatherTodayTemperatureMin_temp',
      comment: '单位摄氏度或华氏度（跟随系统设置）',
      type: 'system',
      tag: 'compound',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      returnValue: '[-99, 99]',
      children: [
        'weatherTodayTemperatureMin',
        'weatherTodayTemperatureMinFahrenheit'
      ],
      unit: '℃/℉',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'weather',
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '今天最低温度（摄氏度）',
      key: 'weatherTodayTemperatureMin',
      comment: '单位摄氏度',
      minValue: -99,
      maxValue: 99,
      type: 'system',
      tag: 'subunit_1',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℃',
      value: () => random(-99, 99),
      jumpAppName: 'weather',
      defaultValue: 20,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },

    {
      label: '今天最低温度（华氏度）',
      key: 'weatherTodayTemperatureMinFahrenheit',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      tag: 'subunit_2',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => random(-147, 211),
      jumpAppName: 'weather',
      defaultValue: 68,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    // 特殊开放数据源
    {
      label: '今天最低温度（华氏度）',
      key: 'weatherTodayTemperatureMinFahrenheit_temp',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => random(-147, 211),
      jumpAppName: 'weather',
      defaultValue: 68,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ['P62', 'P65', 'P67', 'Q61', 'Q63', 'Q66'],
      allowShapeTypes: ['text'],
      onlyUseInPro: true
    },
    {
      label: '明天最高温度',
      key: 'weatherTomorrowTemperatureMax_temp',
      comment: '单位摄氏度或华氏度（跟随系统设置）',
      type: 'system',
      tag: 'compound',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      returnValue: '[-99, 99]',
      children: [
        'weatherTomorrowTemperatureMax',
        'weatherTomorrowTemperatureMaxFahrenheit'
      ],
      unit: '℃/℉',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'weather',
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N62',
        'N62S',
        'N65',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'N65C',
        'N65A',
        'N65B',
        'N67',
        'O65',
        'O65M',
        'O62M',
        'O66',
        'P62',
        'P65',
        'P67',
        'Q66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '明天最高温度（摄氏度）',
      key: 'weatherTomorrowTemperatureMax',
      comment: '单位摄氏度',
      minValue: -99,
      maxValue: 99,
      type: 'system',
      tag: 'subunit_1',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℃',
      value: () => random(-99, 99),
      jumpAppName: 'weather',
      defaultValue: 28,
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N62',
        'N62S',
        'N65',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'N65C',
        'N65A',
        'N65B',
        'N67',
        'O65',
        'O65M',
        'O62M',
        'O66',
        'P62',
        'P65',
        'P67',
        'Q66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '明天最高温度（华氏度）',
      key: 'weatherTomorrowTemperatureMaxFahrenheit',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      tag: 'subunit_2',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => random(-147, 211),
      jumpAppName: 'weather',
      defaultValue: 82,
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N62',
        'N62S',
        'N65',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'N65C',
        'N65A',
        'N65B',
        'N67',
        'O65',
        'O65M',
        'O62M',
        'O66',
        'P62',
        'P65',
        'P67',
        'Q66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '明天最低温度',
      key: 'weatherTomorrowTemperatureMin_temp',
      comment: '单位摄氏度或华氏度（跟随系统设置）',
      type: 'system',
      tag: 'compound',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      returnValue: '[-99, 99]',
      children: [
        'weatherTomorrowTemperatureMin',
        'weatherTomorrowTemperatureMinFahrenheit'
      ],
      unit: '℃/℉',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      jumpAppName: 'weather',
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N62',
        'N62S',
        'N65',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'N65C',
        'N65A',
        'N65B',
        'N67',
        'O65',
        'O65M',
        'O62M',
        'O66',
        'P62',
        'P65',
        'P67',
        'Q66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },

    {
      label: '明天最低温度（摄氏度）',
      key: 'weatherTomorrowTemperatureMin',
      comment: '单位摄氏度',
      minValue: -99,
      maxValue: 99,
      type: 'system',
      tag: 'subunit_1',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℃',
      value: () => random(-99, 99),
      jumpAppName: 'weather',
      defaultValue: 20,
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N62',
        'N62S',
        'N65',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'N65C',
        'N65A',
        'N65B',
        'N67',
        'O65',
        'O65M',
        'O62M',
        'O66',
        'P62',
        'P65',
        'P67',
        'Q66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },

    {
      label: '明天最低温度（华氏度）',
      key: 'weatherTomorrowTemperatureMinFahrenheit',
      comment: '单位华氏度',
      minValue: -147,
      maxValue: 211,
      type: 'system',
      tag: 'subunit_2',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℉',
      value: () => random(-147, 211),
      jumpAppName: 'weather',
      defaultValue: 68,
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N62',
        'N62S',
        'N65',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'N65C',
        'N65A',
        'N65B',
        'N67',
        'O65',
        'O65M',
        'O62M',
        'O66',
        'P62',
        'P65',
        'P67',
        'Q66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '体感温度',
      key: 'weatherCurrentTemperatureFeel',
      comment: '单位摄氏度',
      minValue: -99,
      maxValue: 99,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '℃',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => Number(random(-99, 99, true).toFixed(0)),
      jumpAppName: 'weather',
      defaultValue: 25,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'M62',
        'M65S',
        'M66',
        'O62',
        'O62M',
        'P65',
        'P62',
        'M67',
        'N65',
        'N62',
        'N62S',
        'N66',
        'N60',
        'N67',
        'O65',
        'O65M',
        'O66',
        'O61',
        'O63',
        'Q61',
        'Q63',
        'P67',
        'Q66'
      ]
    },
    {
      label: '湿度',
      key: 'weatherCurrentHumidity',
      comment: '单位%',
      minValue: 0,
      maxValue: 100,
      type: 'system',
      progressAccuracy: [10, 5, 0],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'weather',
      defaultValue: 50,
      needEmptyValue: (deviceType, fillType) => {
        if (!deviceType || !fillType) return false;
        if (fillType == '数字填充') return true;
        else
          return ['O61', 'P62', 'P65', 'P67', 'Q61'].indexOf(deviceType) !== -1;
      },
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-digit'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '天气',
      key: 'weatherCurrentWeather',
      comment:
        '未知、晴、多云、阴、雨、雷、雪、冰雹、雾、霾、阵雨、雨夹雪、小雨、中雨、大雨、暴雨、阵雪、小雪、中雪、大雪、暴雪、浮尘、扬沙、沙尘暴',
      minValue: 0,
      maxValue: 301,
      returnValue: '0、1、2、3...301',
      weatherValueRange: {
        whole: [
          {
            value: 0,
            label: '晴'
          },
          {
            value: 1,
            label: '多云',
            required: true
          },
          {
            value: 2,
            label: '阴',
            required: true
          },
          // {
          //   value: 3,
          //   label: '阵雨'
          // },
          {
            value: 4,
            label: '雷阵雨',
            required: true
          },
          {
            value: 5,
            label: '冰雹',
            required: true
          },
          {
            value: 6,
            label: '雨夹雪'
          },
          // {
          //   value: 301,
          //   label: '雨',
          //   required: true
          // },
          {
            value: 19,
            label: '冻雨',
            required: true
          },
          {
            value: 7,
            label: '小雨',
            values: [7, 301]
          },
          {
            value: 8,
            values: [3, 8, 21],
            label: '中雨'
          },
          {
            value: 9,
            values: [9, 22, 10, 23, 11, 24, 12, 25],
            label: '大雨'
          },
          {
            value: 13,
            label: '小雪',
            values: [13, 14, 33]
          },
          {
            value: 15,
            values: [15, 26],
            label: '中雪'
          },
          {
            value: 16,
            values: [16, 27, 17, 28],
            label: '大雪'
          },
          {
            value: 18,
            values: [18, 57, 58, 32, 49],
            label: '雾',
            required: true
          },
          {
            value: 29,
            label: '浮尘扬沙',
            values: [29, 30]
          },
          {
            value: 20,
            label: '沙尘暴',
            values: [20, 31],
            required: true
          },
          // {
          //   value: 31,
          //   label: '强沙尘暴'
          // },
          {
            value: 53,
            values: [53, 54, 55, 56],
            label: '霾',
            required: true
          },
          {
            value: 99,
            required: true,
            label: '未知'
          }
        ],
        simple: [
          {
            value: 0,
            label: '晴'
          },
          {
            value: 1,
            label: '多云',
            required: true
          },
          {
            value: 2,
            label: '阴',
            required: true
          },
          // {
          //   value: 3,
          //   label: '阵雨'
          // },
          {
            value: 4,
            label: '雷阵雨',
            required: true
          },
          {
            value: 5,
            label: '冰雹',
            required: true
          },
          // {
          //   value: 6,
          //   label: '雨夹雪'
          // },
          {
            value: 301,
            label: '雨',
            values: [3, 301, 19, 7, 8, 21, 9, 22, 10, 23, 11, 24, 12, 25]
          },
          {
            value: 33,
            label: '雪',
            values: [6, 33, 13, 14, 15, 26, 16, 27, 17, 28]
          },
          {
            value: 18,
            values: [18, 57, 58, 32, 49],
            label: '雾',
            required: true
          },
          {
            value: 29,
            label: '沙尘暴',
            values: [29, 30, 20, 31]
          },
          // {
          //   value: 31,
          //   label: '强沙尘暴'
          // },
          {
            value: 53,
            values: [53, 54, 55, 56],
            label: '霾',
            required: true
          },
          {
            value: 99,
            required: true,
            label: '未知'
          }
        ]
      },
      type: 'system',
      allowShapeTypes: ['image-array-normal', 'text'],
      defaultValue: 1,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal'].includes(shapeType);
            }
            case 'AOD': {
              return [''].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '天色完整版',
      key: DataSourceKey.tianSeFull,
      comment: '天色完整版',
      minValue: 0,
      maxValue: 39,
      returnValue: '[0,39]',
      type: 'system',
      includeDeviceTypes: ElectronApi.isProfessional()
        ? ['Q61', 'Q66', 'Q63']
        : ['Q66'],
      needEmptyValue: () => {
        return true;
      },
      allowShapeTypes: ['image-array-normal', 'text']
    },
    {
      label: '天色精简版',
      key: DataSourceKey.tianseSimple,
      comment: '天色精简版',
      minValue: 0,
      maxValue: 24,
      returnValue: '[0,24]',
      type: 'system',
      includeDeviceTypes: ElectronApi.isProfessional()
        ? ['Q61', 'Q66', 'Q63']
        : ['Q66'],
      needEmptyValue: () => {
        return true;
      },
      allowShapeTypes: ['image-array-normal', 'text']
    },
    {
      label: '风向',
      key: 'weatherCurrentWindDirection',
      comment:
        '0-9，分别代表：无持续⻛向、旋转⻛、北⻛、东北⻛、东⻛、东南⻛、南⻛、西南⻛、西⻛、西北⻛',
      minValue: 0,
      maxValue: 9,
      labelRange: [
        '无持续⻛向',
        '旋转⻛',
        '北⻛',
        '东北⻛',
        '东⻛',
        '东南⻛',
        '南⻛',
        '西南⻛',
        '西⻛',
        '西北⻛'
      ],
      type: 'system',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      value: () => random(0, 9),
      jumpAppName: 'weather',
      defaultValue: 8,
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C'],
      needEmptyValue: (deviceType) => {
        if (!deviceType) return false;
        return ['O61', 'P62', 'P65', 'P67', 'Q61', 'Q66', 'Q63'].indexOf(deviceType) !== -1;
      }
    },
    {
      label: '风向角',
      key: 'weatherCurrentWindAngle',
      comment: '当前⻛向⻆，0~359°，360表示无持续⻛向，361表示旋转⻛',
      minValue: 0,
      maxValue: 361,
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      type: 'system',
      unit: '°',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      defaultValue: 0,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'O65',
        'O65M',
        'M62',
        'M65S',
        'M66',
        'P65'
      ]
    },
    // {
    //   label: '风速',
    //   key: 'weatherCurrentWindSpeed',
    //   comment: '单位km/h',
    //   minValue: 0,
    //   maxValue: 999,
    //   formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
    //   type: 'system',
    //   unit: 'km/h',
    //   allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
    //   value: () => random(0, 999),
    //   jumpAppName: 'weather',
    //   defaultValue: 10,
    //   needEmptyValue: () => {
    //     return true;
    //   },
    //   // excludeDeviceTypes: ['N65A', 'N65B', 'N65C', 'M62', 'M65S', 'M66'],
    //   includeDeviceTypes: []
    // },
    {
      label: '风力级别',
      key: 'weatherCurrentWindLevel',
      comment:
        '0-12，分别代表：微⻛、 1级、 2级、 3级、 4级、 5级、 6级、 7级、 8级、 9级、 10级、 11级、 12级及以上',
      minValue: 0,
      maxValue: 12,
      labelRange: [
        '微⻛',
        '1级',
        '2级',
        '3级',
        '4级',
        '5级',
        '6级',
        '7级',
        '8级',
        '9级',
        '10级',
        '11级',
        '12级及以上'
      ],
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '级',
      allowShapeTypes: ['*'],
      value: () => random(0, 12),
      jumpAppName: 'weather',
      defaultValue: 2,
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C'],
      needEmptyValue: (deviceType) => {
        if (!deviceType) return false;
        return ['O61', 'P62', 'P65', 'P67', 'Q61', 'Q63', 'Q66'].indexOf(deviceType) !== -1;
      }
    },
    {
      label: 'AQI',
      key: 'weatherCurrentAirQualityIndex',
      comment: '空气质量',
      minValue: 0,
      maxValue: 500,
      type: 'system',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(0, 500),
      jumpAppName: 'weather',
      defaultValue: 40,
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '空气质量指数等级（通过AQI模拟计算）',
      key: 'weatherCurrentAirQualityLevel',
      comment:
        '0-5，分别代表：优，良、 轻度污染、 中度污染、 重度污染、 严重污染',
      minValue: 0,
      maxValue: 5,
      labelRange: ['优', '良', '轻度污染', '中度污染', '重度污染', '严重污染'],
      type: 'system',
      allowShapeTypes: ['image-array-normal'],
      value: () => random(0, 5),
      jumpAppName: 'weather',
      defaultValue: 0,
      excludeDeviceTypes: ['M62', 'M65S', 'M66'],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '空气质量等级',
      key: 'weatherAirQualityLevel',
      comment:
        '0-5，分别代表：优，良、 轻度污染、 中度污染、 重度污染、 严重污染。固件直接下发等级值',
      minValue: 0,
      maxValue: 5,
      labelRange: ['优', '良', '轻度污染', '中度污染', '重度污染', '严重污染'],
      type: 'system',
      allowShapeTypes: ['image-array-normal','text','image'],
      value: () => random(0, 5),
      jumpAppName: 'weather',
      defaultValue: 0,
      includeDeviceTypes: ElectronApi.isProfessional()
        ? ['Q61', 'P62', 'Q63']
        : [ 'P62'],
      needEmptyValue: () => {
        return true;
      }
    },
    // {
    //   label: '降雨概率',
    //   key: 'weatherCurrentChanceOfRain',
    //   comment: '',
    //   minValue: 0,
    //   maxValue: 100,
    //   type: 'system',
    //   unit: '%',
    //   progressAccuracy: [10, 5],
    //   formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
    //   allowShapeTypes: ['*'],
    //   value: () => random(0, 100),
    //   jumpAppName: 'weather',
    //   defaultValue: 30,
    //   includeDeviceTypes: [],
    //   needEmptyValue: () => {
    //     return true;
    //   }
    // },
    {
      label: '海平面气压',
      key: 'weatherCurrentPressure',
      comment: '当地海平面气压，单位hPa，百帕',
      minValue: 700,
      maxValue: 1100,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'hPa',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(700, 1100),
      defaultValue: 1013,
      excludeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'N66',
        'O65',
        'O65M',
        'M62',
        'M65S',
        'M66'
      ],
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '海拔高度',
      key: 'systemSensorFusionAltitude',
      comment: '单位m',
      minValue: -200,
      maxValue: 9000,
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      type: 'system',
      unit: 'm',
      allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
      value: () => random(-200, 9000),
      defaultValue: 83,
      includeDeviceTypes: [
        'N62S',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'P62'
      ],
      needEmptyValue: () => {
        return true;
      }
    },
    // {
    //   label: '能见度',
    //   key: 'weatherCurrentVisibility',
    //   comment: '单位m',
    //   minValue: 0,
    //   maxValue: 999,
    //   formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
    //   type: 'system',
    //   unit: 'm',
    //   allowShapeTypes: ['image', 'image-array-digit', 'progress-bar', 'text'],
    //   value: () => random(0, 999),
    //   jumpAppName: 'weather',
    //   needEmptyValue: () => {
    //     return true;
    //   },
    //   includeDeviceTypes: [],
    //   defaultValue: 15
    // },
    {
      label: '紫外线指数',
      key: 'weatherCurrentUVIndex',
      comment: '',
      minValue: 0,
      maxValue: 15,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_SPF8],
      unit: 'SPF',
      allowShapeTypes: ['*'],
      value: () => random(0, 15),
      jumpAppName: 'weather',
      defaultValue: 6,
      needEmptyValue: () => {
        return true;
      },
      excludeDeviceTypes: ['M66', 'N65A', 'N65B', 'N65C']
    },
    {
      label: '紫外线UVA',
      key: 'healthUVA',
      comment: '晒黑',
      minValue: 1,
      maxValue: 5,
      labelRange: ['低', '较低', '较高', '高', '极高'],
      type: 'health',
      formats: [DataSourceFormat.Format_8],
      allowShapeTypes: ['image-array-normal'],
      value: () => random(1, 5),
      jumpAppName: 'uvSensor',
      defaultValue: 2,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['Q63'] : []
    },
    {
      label: '紫外线UVB',
      key: 'healthUVB',
      comment: '晒伤',
      minValue: 1,
      maxValue: 5,
      labelRange: ['低', '较低', '较高', '高', '极高'],
      type: 'health',
      formats: [DataSourceFormat.Format_8],
      allowShapeTypes: ['image-array-normal'],
      value: () => random(1, 5),
      jumpAppName: 'uvSensor',
      defaultValue: 2,
      needEmptyValue: () => {
        return true;
      },
      includeDeviceTypes: ElectronApi.isProfessional() ? ['Q63'] : []
    },
    // {
    //   label: '穿衣指数',
    //   key: 'weatherCurrentDressIndex',
    //   comment:
    //     '0-7，分别代表：适宜短袖、适宜衬衫、适宜薄外套、适宜夹克、适宜风衣、适宜棉衣、适宜冬大衣、适宜羽绒服',
    //   minValue: 0,
    //   maxValue: 7,
    //   labelRange: [
    //     '适宜短袖',
    //     '适宜衬衫',
    //     '适宜薄外套',
    //     '适宜夹克',
    //     '适宜风衣',
    //     '适宜棉衣',
    //     '适宜冬大衣',
    //     '适宜羽绒服'
    //   ],
    //   type: 'system',
    //   allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
    //   value: () => random(0, 7),
    //   jumpAppName: 'weather',
    //   defaultValue: 2,
    //   needEmptyValue: () => {
    //     return true;
    //   },
    //   includeDeviceTypes: []
    // },
    {
      label: '电量',
      key: 'systemStatusBattery',
      comment: '单位%',
      minValue: 0,
      maxValue: 100,
      type: 'system',
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '%',
      progressAccuracy: [10, 5, 0],
      allowShapeTypes: ['*'],
      value: () => random(0, 100),
      jumpAppName: 'settings',
      defaultValue: 80,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            case 'AOD': {
              return ['image-array-normal', 'image-array-digit'].includes(
                shapeType
              );
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '当前充电状态',
      key: 'systemStatusCharge',
      comment: '0：没有充电，1：正在充电',
      minValue: 0,
      maxValue: 1,
      labelRange: ['没有充电', '正在充电', 'TBD', 'TBD'],
      type: 'system',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      value: () => random(0, 1),
      jumpAppName: 'settings',
      excludeDeviceTypes: ['M62', 'M65S', 'M66', 'N65A', 'N65B', 'N65C'],
      defaultValue: 0
    },
    {
      label: '勿扰模式',
      key: 'systemStatusDisturb',
      comment: '0：未开启， 1：已开启',
      minValue: 0,
      maxValue: 1,
      labelRange: ['未开启', '已开启'],
      type: 'system',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      value: () => random(0, 1),
      jumpAppName: 'settings',
      defaultValue: 0,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal'].includes(shapeType);
            }
            case 'AOD': {
              return ['image-array-normal'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      },
      excludeDeviceTypes: ['N65A', 'N65B', 'N65C']
    },
    {
      label: '蓝牙状态',
      key: 'systemStatusBluetooth',
      comment: '0：未连接，1：已连接',
      minValue: 0,
      maxValue: 1,
      labelRange: ['未连接', '已连接'],
      type: 'system',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      value: () => random(0, 1),
      jumpAppName: 'settings',
      excludeDeviceTypes: [
        'M67',
        'M62',
        'M65S',
        'N65A',
        'N65B',
        'N65C',
        'N65',
        'N62'
      ],
      defaultValue: 0,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal'].includes(shapeType);
            }
            case 'AOD': {
              return ['image-array-normal'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: 'Wifi状态',
      key: 'systemStatusWifi',
      comment: '0：未连接，1：已连接',
      minValue: 0,
      maxValue: 1,
      labelRange: ['未连接', '已连接'],
      type: 'system',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      value: () => random(0, 1),
      jumpAppName: 'settings',
      excludeDeviceTypes: [
        'N65A',
        'N65B',
        'N65C',
        'M67',
        'N62',
        'N65',
        'N60',
        'N62S',
        'N66',
        'M62',
        'M65S',
        'M66',
        'O61',
        'O62',
        'O63',
        'O62M',
        'O66',
        'N67',
        'P65',
        'P67',
        'P62',
        'Q66'
      ],
      defaultValue: 0
    },
    {
      label: '屏幕lock',
      key: 'systemStatusScreenLock',
      comment: '0：屏幕已解锁，1：屏幕已锁定',
      minValue: 0,
      maxValue: 1,
      labelRange: ['屏幕已解锁', '屏幕已锁定'],
      type: 'system',
      allowShapeTypes: ['image', 'image-array-normal', 'progress-bar', 'text'],
      value: () => random(0, 1),
      jumpAppName: 'settings',
      excludeDeviceTypes: [
        'M67',
        'N62',
        'N65',
        'N60',
        'M62',
        'M65S',
        'N67',
        'N65A',
        'N65B',
        'N65C'
      ],
      defaultValue: 0,
      allowShapeTypeFn: ({ deviceType, shapeType, themeType }) => {
        if (deviceType == 'M66') {
          switch (themeType) {
            case 'normal': {
              return ['image-array-normal'].includes(shapeType);
            }
            case 'AOD': {
              return ['image-array-normal'].includes(shapeType);
            }
            default:
              return true;
          }
        }
        return true;
      }
    },
    {
      label: '最近的闹钟时间',
      key: 'appAlarm',
      comment: '',
      minValue: 0,
      maxValue: 24,
      type: 'system',
      allowShapeTypes: ['image-array-digit', 'text'],
      defaultValue: 7.5,
      returnValue: '[00:00, 24:00)',
      canNotReachMaxValue: true,
      formats: [
        DataSourceFormat['Format_8:00'],
        DataSourceFormat['Format_08:00']
      ],
      unit: ':',
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N65',
        'M62',
        'M65S',
        'M66',
        'N65A',
        'N65B',
        'N65C',
        'O63',
        'P62',
        'N62',
        'O62',
        'O62M',
        'O65',
        'O65M'
      ],
      needEmptyValue: () => {
        return true;
      },
      widgetImageArray: 'time'
    },
    {
      label: '最近的闹钟时间小时部分',
      key: 'appAlarmHour',
      comment: '最近的闹钟时间的小时部分',
      minValue: 0,
      maxValue: 23,
      type: 'system',
      allowShapeTypes: ['image-array-digit', 'text'],
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '时',
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N65',
        'M62',
        'M65S',
        'M66',
        'N65A',
        'N65B',
        'N65C',
        'O63',
        'P62',
        'N62',
        'O62',
        'O62M',
        'O65',
        'O65M'
      ],
      defaultValue: 7,
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '最近的闹钟时间分钟部分',
      key: 'appAlarmMinute',
      comment: '最近的闹钟时间的分钟部分',
      minValue: 0,
      maxValue: 59,
      type: 'system',
      allowShapeTypes: ['image-array-digit', 'text'],
      formats: [
        DataSourceFormat.Format_8,
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时
      ],
      unit: '分',
      excludeDeviceTypes: [
        'M67',
        'N60',
        'N65',
        'M62',
        'M65S',
        'M66',
        'N65A',
        'N65B',
        'N65C',
        'O63',
        'P62',
        'N62',
        'O62',
        'O62M',
        'O65',
        'O65M'
      ],
      defaultValue: 30,
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '指南针角度',
      key: 'systemSensorCompass',
      comment: '指南针角度，[0,359]',
      minValue: 0,
      maxValue: 359,
      returnValue: '[0,359]',
      type: 'system',
      allowShapeTypes: ['image-array-digit', 'image', 'progress-bar'],
      unit: '°',
      includeDeviceTypes: ElectronApi.isProfessional()
        ? [
            'N62S',
            'O61',
            'P65',
            'P62',
            'O62',
            'O63',
            'Q61',
            'Q63',
            'Q66',
            'O62M',
            'P67'
          ]
        : ['N62S', 'O61', 'P65', 'P62', 'O62', 'O63', 'O62M', 'P67'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      },
      defaultValue: 0,
      needEmptyValue: (deviceType) => {
        if (!deviceType) return false;
        return (
          ['N62S', 'O62', 'O63', 'Q63', 'O62M', 'P67'].indexOf(deviceType) !==
          -1
        );
      }
    },
    {
      label: '指南针方向',
      key: 'systemSensorCompassDirection',
      comment:
        '337°≤r<22°=北，22°≤r<67°=东北，67°≤r<112°=东，112°≤r<157°=东南，157°≤r<202°=南，202°≤r<247°=西南，247°≤r<292°=西，292°≤r<337°=西北',
      returnValue: '北，东北，东，东南，南，西南，西，西北',
      type: 'system',
      allowShapeTypes: ['image-array-normal'],
      includeDeviceTypes: ['O61', 'P65', 'P62', 'P67', 'Q61', 'Q63', 'Q66'],
      allowShapeTypeFn: ({ themeType }) => {
        if (themeType == 'AOD') return false;
        return true;
      },
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '实时气压',
      key: 'systemSensorAtmosphericPressure',
      comment: '实时气压，单位hPa（百帕）',
      minValue: 300,
      maxValue: 1100,
      type: 'system',
      allowShapeTypes: ['*'],
      formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: 'hPa',
      includeDeviceTypes: [
        'N62S',
        'O61',
        'O62',
        'O63',
        'Q61',
        'Q63',
        'O62M',
        'P62'
      ],
      defaultValue: 990,
      needEmptyValue: () => {
        return true;
      }
    },
    {
      label: '禁飞时间',
      key: 'appSportNoFlyTime',
      comment: `取值范围取决于禁飞时间单位，
      分钟：展示到个位数；
      小时：展示到小数点后两位；
      天：展示到小数点后两位`,
      minValue: 0,
      maxValue: 24,
      canNotReachMaxValue: true,
      returnValue: '[0,24)',
      precision: 2,
      type: 'system',
      allowShapeTypes: ['image-array-digit'],
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N62S'] : [],
      // 这里的默认值是以分钟为单位的
      defaultValue: 18 * 60
    },
    {
      label: '禁飞时间-格式',
      key: 'appSportNoFlyTimeUnit',
      comment: `1：分钟（禁飞时间小于1小时）；2：小时（禁飞时间介于1小时~24小时）；3：天（禁飞时间在24小时以上；0：禁飞时间超过3天`,
      minValue: 0,
      maxValue: 3,
      type: 'system',
      allowShapeTypes: ['image-array-digit'],
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N62S'] : [],
      labelRange: ['未知', '分钟', '小时', '天']
    },
    {
      label: '水面间隔时间',
      key: 'appSportSitTime',
      comment: `取值范围取决于水面间隔时间单位，
      分钟：展示到个位数；
      小时：展示到小数点后两位；
      天：展示到小数点后两位`,
      minValue: 0,
      maxValue: 24,
      canNotReachMaxValue: true,
      returnValue: '[0,24)',
      precision: 2,
      type: 'system',
      allowShapeTypes: ['image-array-digit'],
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N62S'] : [],
      // 这里的默认值是以分钟为单位的
      defaultValue: 35
    },
    {
      label: '水面间隔时间-格式',
      key: 'appSportSitTimeUnit',
      comment: `1：分钟（水面间隔时间小于1小时）；2：小时（水面间隔时间介于1小时~24小时）；3：天（水面间隔时间在24小时以上；0：水面间隔时间超过3天或未潜水`,
      minValue: 0,
      maxValue: 3,
      type: 'system',
      allowShapeTypes: ['image-array-digit'],
      includeDeviceTypes: ElectronApi.isProfessional() ? ['N62S'] : [],
      labelRange: ['未知', '分钟', '小时', '天']
    },
    {
      label: '屏幕状态',
      key: 'systemScreenStatus',
      comment: '屏幕状态，0：灭屏，1：AOD， 2:亮屏',
      minValue: 0,
      maxValue: 2,
      type: 'system',
      allowShapeTypes: ['image-array-normal', 'image', 'progress-bar'],
      defaultValue: 2,
      labelRange: ['灭屏', 'AOD', '亮屏'],
      includeDeviceTypes: ['O61', 'P62', 'Q61']
    },
    {
      label: '手腕翻滚角',
      key: 'systemSensorWristRoll',
      comment: '手腕翻滚角，绕X轴旋转，[-90,90]',
      minValue: -90,
      maxValue: 90,
      type: 'system',
      allowShapeTypes: ['image-array-digit', 'image', 'progress-bar'],
      // formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '°',
      // includeDeviceTypes: ['N62S', 'O61', 'P65', 'P62', 'O62', 'O63', 'Q63', 'O62M'],
      defaultValue: 0,
      includeDeviceTypes: ['O61', 'P62', 'Q61']
    },
    {
      label: '手腕俯仰角',
      key: 'systemSensorWristPitch',
      comment: '手腕俯仰角，绕Y轴旋转，[-90,90]',
      minValue: -90,
      maxValue: 90,
      type: 'system',
      allowShapeTypes: ['image-array-digit', 'image', 'progress-bar'],
      // formats: [DataSourceFormat.Format_8, DataSourceFormat.Format_8时],
      unit: '°',
      // includeDeviceTypes: ['N62S', 'O61', 'P65', 'P62', 'O62', 'O63', 'Q63', 'O62M'],
      defaultValue: 0,
      includeDeviceTypes: ['O61', 'P62', 'Q61']
    },
    {
      label: '动画状态', // 仅表盘工具渲染rive动画需要
      key: 'interactiveState',
      comment:
        '0：有动画回默认状态；1：有动画进入交互状态；2：无动画回默认状态',
      minValue: 0,
      maxValue: 2,
      type: 'system',
      allowShapeTypes: [],
      labelRange: [
        '有动画回默认状态',
        '有动画进入交互状态',
        '无动画回默认状态'
      ],
      includeDeviceTypes: ['O61', 'P62', 'Q61']
    }
  ]
  // 设置: []
};
export type SourceKey = (typeof dataSourceList)[string][number]['key'];
export const dataSourceWholeItems = Object.values(dataSourceList).flat();
